import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../css/UnitList.css";
import "../css/PurchaseList.css";
import { FaEdit, FaTrash } from "react-icons/fa";

const ProductPurchaseReport = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || "";

  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("all");
  const [purchases, setPurchases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showModal, setShowModal] = useState(false);
  const [purchaseToDelete, setPurchaseToDelete] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState("all");
  const [productName, setProductName] = useState("");
  const [unitId, setUnitId] = useState("");
  const [units, setUnits] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [purchaseToEdit, setPurchaseToEdit] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const rowsPerPage = 20;

  useEffect(() => {
    const fetchUnits = async () => {
      setIsLoadingUnits(true);
      try {
        const response = await fetch(`${API_URL}add-unit-list.php`);
        const data = await response.json();
        if (data.units) {
          setUnits(data.units);
        } else {
          setUnits([]);
        }
      } catch (error) {
        setUnits([]);
      } finally {
        setIsLoadingUnits(false);
      }
    };
    fetchUnits();
  }, [API_URL]);

  const handleAddProductSubmit = async (e) => {
    e.preventDefault();
    if (!productName.trim()) {
      showToast("Please enter a product name", "error");
      return;
    }
    if (!unitId) {
      showToast("Please select a unit", "error");
      return;
    }
    setIsSubmitting(true);
    setIsLoadingProduct(true);
    try {
      const formData = new FormData();
      formData.append("product", productName.trim());
      formData.append("unitname_id", unitId);
      const response = await fetch(`${API_URL}add-product.php`, {
        method: "POST",
        body: formData,
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status) {
          showToast(`Product "${productName}" added successfully!`, "success");
          setProductName("");
          setUnitId("");
          const prodRes = await fetch(`${API_URL}product-list.php`);
          const prodData = await prodRes.json();
          if (prodData.status && Array.isArray(prodData.data)) {
            setProducts(prodData.data);
          }
        } else {
          showToast(
            result.message || "Error adding product. Please try again.",
            "error"
          );
        }
      } else {
        showToast("Network error. Please try again.", "error");
      }
    } catch (error) {
      showToast("Error saving product. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
      setIsLoadingProduct(false);
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API_URL}product-list.php`);
        const data = await response.json();
        if (data.status && Array.isArray(data.data)) {
          setProducts(data.data);
        }
      } catch (error) {
        showToast("Failed to load products", "error");
      }
    };
    fetchProducts();
  }, [API_URL]);

  useEffect(() => {
    if (!selectedProductId) {
      setPurchases([]);
      return;
    }

    const fetchPurchases = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_URL}add-list-purchases.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ product_id: selectedProductId }),
        });

        const data = await response.json();

        if (data.status === true && Array.isArray(data.data)) {
          setPurchases(data.data);
        } else {
          setPurchases([]);
          showToast("No purchase history found for this product.", "info");
        }
      } catch (error) {
        showToast("Network error. Please try again.", "error");
      } finally {
        setIsLoading(false);
      }
    };
    fetchPurchases();
  }, [selectedProductId, API_URL]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const openDeleteModal = (purchase_id) => {
    setPurchaseToDelete(purchase_id);
    setShowModal(true);
  };

  const closeDeleteModal = () => {
    setShowModal(false);
    setPurchaseToDelete(null);
  };

  const handleDelete = async () => {
    if (!purchaseToDelete) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}delete-purchase.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ purchase_id: purchaseToDelete }),
      });
      const data = await response.json();
      if (data.status === true) {
        setPurchases((prev) => prev.filter((p) => p.id !== purchaseToDelete));
        showToast("Purchase deleted successfully.", "success");
      } else {
        showToast(data.message || "Failed to delete purchase.", "error");
      }
    } catch (error) {
      showToast("Network error. Please try again.", "error");
    } finally {
      setIsLoading(false);
      closeDeleteModal();
    }
  };

  const invoiceNumbers = Array.from(
    new Set(purchases.map((p) => p.invoice_no))
  );

  const filteredPurchases = purchases.filter((purchase) => {
    const purchaseDate = new Date(purchase.purchase_date);
    const isAfterStartDate = !startDate || purchaseDate >= new Date(startDate);
    const isBeforeEndDate = !endDate || purchaseDate <= new Date(endDate);
    const isInvoiceMatch =
      selectedInvoice === "all" || purchase.invoice_no === selectedInvoice;

    return isAfterStartDate && isBeforeEndDate && isInvoiceMatch;
  });

  const totalPages = Math.ceil(filteredPurchases.length / rowsPerPage);
  const paginatedPurchases = filteredPurchases.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProductId, selectedInvoice, startDate, endDate]);

  const totalAmount = filteredPurchases.reduce(
    (sum, purchase) => sum + Number(purchase.total_amt),
    0
  );

  return (
    <div className="category-list-content">
      <div
        className="page-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div className="header-content">
          <h1>Product Purchase Report</h1>
          <p>Select a product to view its purchase history</p>
        </div>
      </div>

      <div
        className="form-container "
        style={{
          marginBottom: "30px",
          width: "100%",
          display: "flex",
          gap: "20px",
          justifyContent: "space-around",
          maxWidth: "100%",
          flexWrap: "wrap",
        }}
      >
        <div className="form-group">
          <label htmlFor="product-select" style={{ fontSize: "1.2em" }}>
            Select Product
          </label>
          <select
            id="product-select"
            value={selectedProductId}
            onChange={(e) => setSelectedProductId(e.target.value)}
          >
            <option value="">-- Choose a Product --</option>
            <option value="all">All Product</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.product}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="invoice-select" style={{ fontSize: "1.2em" }}>
            Filter by Invoice Number
          </label>
          <select
            id="invoice-select"
            value={selectedInvoice}
            onChange={(e) => setSelectedInvoice(e.target.value)}
            style={{ width: "100%" }}
          >
            <option value="all">All Invoices</option>
            {invoiceNumbers.map((inv) => (
              <option key={inv} value={inv}>
                {inv}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="start-date" style={{ fontSize: "1.2em" }}>
            Start Date
          </label>
          <input
            type="date"
            id="start-date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "5px",
              border: "1px solid #ccc",
            }}
          />
        </div>
        <div className="form-group">
          <label htmlFor="end-date" style={{ fontSize: "1.2em" }}>
            End Date
          </label>
          <input
            type="date"
            id="end-date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "5px",
              border: "1px solid #ccc",
            }}
          />
        </div>
      </div>

      {isLoading ? (
        <p>Loading purchase history...</p>
      ) : (
        selectedProductId && (
          <div className="unit-list-table-container purchase-table-container">
            <table className="unit-list-table purchase-table">
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>Supplier</th>
                  <th>Invoice No</th>
                  <th>Qty</th>
                  <th>Total Amount</th>
                  <th>Purchase Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedPurchases.length > 0 ? (
                  paginatedPurchases.map((purchase) => (
                    <tr key={purchase.id}>
                      <td>{purchase.product_name}</td>
                      <td>{purchase.supplier_name}</td>
                      <td>{purchase.invoice_no}</td>
                      <td>
                        {purchase.quantity} {purchase.unit_name}
                      </td>
                      <td>{purchase.total_amt}</td>
                      <td>
                        {new Date(purchase.purchase_date).toLocaleDateString()}
                      </td>
                      <td className="action-cell">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() =>
                            navigate(`/edit-purchase/${purchase.id}`, {
                              state: { purchase },
                            })
                          }
                          style={{ marginRight: "5px" }}
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => openDeleteModal(purchase.id)}
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7">
                      No purchase records found for the selected filters.
                    </td>
                  </tr>
                )}
                {filteredPurchases.length > 0 && (
                  <tr style={{ background: "#ffeeba", fontWeight: "bold" }}>
                    <td colSpan="4" style={{ textAlign: "right" }}>
                      Total Amount
                    </td>
                    <td>{totalAmount}</td>
                    <td colSpan="2"></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )
      )}

      {selectedProductId && totalPages > 1 && (
        <div className="pagination-container">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            &lt; Prev
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next &gt;
          </button>
        </div>
      )}

      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              ×
            </button>
          </div>
        </div>
      )}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete this purchase?</p>
            <div className="modal-actions">
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isLoading}
              >
                {isLoading ? "Deleting..." : "Confirm"}
              </button>
              <button
                className="btn btn-secondary"
                onClick={closeDeleteModal}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPurchaseReport;
